import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import {
  Award, Download, Share2, CheckCircle, FileText, XCircle, WifiOff, ChevronRight,
  BadgeCheck, Clock, UploadCloud, AlertTriangle, Info, ShieldAlert, Linkedin,
} from 'lucide-react';
import { motion, useReducedMotion } from 'framer-motion';
import axios from 'axios';
import { staggerContainer, fadeUp, maybe } from '../lib/motionVariants';
import { getApiErrorMessage } from '../utils/apiError';
import { Badge } from '../components/ui/badge';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Dijital Rozet (T.C. Sanayi ve Teknoloji Bakanlığı) sertifika durumu →
// insan-dili görsel eşleme. Teknik durum adı (dr_status) ekranda ASLA
// görünmez — yalnızca data-testid'de. Mevcut Badge varyantlarını kullanır,
// yeni hex renk icat edilmedi (bkz. components/ui/badge.jsx).
const DR_STATUS_CONFIG = {
  pending: { icon: Clock, badgeVariant: 'secondary', iconColor: 'text-gray-500', titleKey: 'certificates.dr.pending.title', bodyKey: 'certificates.dr.pending.body' },
  publishing: { icon: UploadCloud, badgeVariant: 'secondary', iconColor: 'text-gray-500', titleKey: 'certificates.dr.publishing.title', bodyKey: 'certificates.dr.publishing.body' },
  sent: { icon: BadgeCheck, badgeVariant: 'success', iconColor: 'text-emerald-600', titleKey: 'certificates.dr.sent.title', bodyKey: 'certificates.dr.sent.body' },
  failed: { icon: AlertTriangle, badgeVariant: 'danger', iconColor: 'text-rose-600', titleKey: 'certificates.dr.failed.title', bodyKey: 'certificates.dr.failed.body' },
  skipped: { icon: Info, badgeVariant: 'secondary', iconColor: 'text-gray-500', titleKey: 'certificates.dr.skipped.title', bodyKey: 'certificates.dr.skipped.body' },
  awaiting_consent: { icon: ShieldAlert, badgeVariant: 'warning', iconColor: 'text-amber-600', titleKey: 'certificates.dr.awaitingConsent.title', bodyKey: 'certificates.dr.awaitingConsent.body' },
  revoked: { icon: XCircle, badgeVariant: 'danger', iconColor: 'text-rose-600', titleKey: 'certificates.dr.revoked.title', bodyKey: 'certificates.dr.revoked.body' },
};

export default function CertificatesPage() {
  const { lang, t } = useLanguage();
  const { authHeaders, user } = useAuth();
  const toast = useToast();
  const reduced = useReducedMotion();
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [linkedinSharingId, setLinkedinSharingId] = useState(null);
  const [badgeImgErrored, setBadgeImgErrored] = useState(() => new Set());

  const fetchCerts = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await axios.get(`${API}/certificates`, { headers: authHeaders() });
      setCertificates(res.data.certificates);
    } catch (err) {
      console.error(err);
      setError(true);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    document.body.classList.add('duo-page');
    fetchCerts();
    return () => document.body.classList.remove('duo-page');
  }, [authHeaders]); // eslint-disable-line react-hooks/exhaustive-deps

  // Blob response'undaki hata gövdesini JSON'a parse eder
  const parseBlobError = async (err) => {
    try {
      const blob = err?.response?.data;
      if (blob instanceof Blob) {
        const text = await blob.text();
        const json = JSON.parse(text);
        return json?.message_tr || json?.message_en || json?.detail || null;
      }
    } catch {
      // parse hatası — yoksay
    }
    return null;
  };

  const viewPdf = async (certId) => {
    try {
      const res = await axios.get(`${API}/certificates/${certId}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank');
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error(t('certificates.error.auth'));
      } else {
        const parsed = await parseBlobError(err);
        toast.error(parsed || t('certificates.error.downloadFailed'));
      }
    }
  };

  const downloadPdf = async (certId, cert) => {
    try {
      const res = await axios.get(`${API}/certificates/${certId}/pdf`, {
        headers: authHeaders(),
        responseType: 'blob',
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      const courseTitle = (lang === 'tr' ? cert?.course_title_tr || cert?.course_title : cert?.course_title) || certId;
      const safeName = courseTitle.replace(/[^a-zA-Z0-9ğüşıöçĞÜŞİÖÇ\s-]/g, '').trim().replace(/\s+/g, '-');
      a.download = `sertifika-${safeName}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401 || status === 403) {
        toast.error(t('certificates.error.auth'));
      } else {
        const parsed = await parseBlobError(err);
        toast.error(parsed || t('certificates.error.downloadFailed'));
      }
    }
  };

  const shareCertificate = (cert) => {
    const title = lang === 'tr' ? cert.course_title_tr || cert.course_title : cert.course_title;
    const text = t('certificates.share.text')
      .replace('{{title}}', title)
      .replace('{{code}}', cert.verification_code);
    if (navigator.share) {
      navigator.share({ title: 'Kariyer Online Certificate', text }).catch(() => {});
    } else {
      navigator.clipboard.writeText(text).then(() => toast.success(t('certificates.share.copied')));
    }
  };

  // Dijital Rozet — LinkedIn paylaşımı. Genel Share2 butonundan (native
  // share/clipboard, backend'e gitmez) AYRI: burada gerçek backend paylaşım
  // ucu (POST /certificates/{id}/share) çağrılır ve dönen share_url LinkedIn'in
  // kendi paylaşım sayfasında açılır. Yalnızca dr_status === 'sent' iken
  // gösterilir; iptal edilmiş (cert.status === 'revoked') sertifikalarda
  // devre dışı bırakılır.
  const shareDijitalRozetLinkedin = async (cert) => {
    if (cert.status === 'revoked' || linkedinSharingId) return;
    setLinkedinSharingId(cert.certificate_id);
    try {
      const res = await axios.post(
        `${API}/certificates/${cert.certificate_id}/share`,
        { channel: 'linkedin' },
        { headers: authHeaders() }
      );
      const shareUrl = res.data?.share_url;
      if (shareUrl) {
        const linkedinUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
        window.open(linkedinUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, lang) || t('certificates.dr.linkedin.error'));
    } finally {
      setLinkedinSharingId(null);
    }
  };

  // Bakanlığın verdiği rozet görseli yüklenemezse (onError) fallback ikona düşer.
  const markBadgeImageError = (certId) => {
    setBadgeImgErrored((prev) => {
      const next = new Set(prev);
      next.add(certId);
      return next;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <div
          className="max-w-4xl mx-auto px-4 py-8"
          aria-label={t('certificates.loading.ariaLabel')}
          data-testid="certificates-loading"
        >
          <div className="h-9 w-56 bg-[var(--duo-stroke)] rounded animate-pulse mb-8" />
          <div className="space-y-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="card-duo animate-pulse h-36" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-white">
        <div className="max-w-4xl mx-auto px-4 py-8" data-testid="certificates-error">
          <div className="card-duo p-12 text-center">
            <WifiOff size={40} className="mx-auto text-[var(--text-muted)] mb-4" aria-hidden="true" />
            <p className="font-duo-display text-lg text-[var(--text-main)] mb-1">
              {t('certificates.error.title')}
            </p>
            <p className="text-sm text-[var(--text-muted)] mb-4">{t('certificates.error.sub')}</p>
            <button onClick={fetchCerts} className="btn-duo-blue">
              {t('common.retry')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="certificates-page">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <h1 className="font-duo-display text-3xl mb-8 flex items-center gap-3">
          <Award size={28} strokeWidth={2.5} className="text-[var(--brand-accent)]" aria-hidden="true" />
          {t('certificates.pageTitle')}
        </h1>

        {certificates.length === 0 ? (
          <div className="card-duo p-12 text-center" data-testid="certificates-empty">
            <Award size={48} className="mx-auto text-[var(--text-muted)] mb-4" aria-hidden="true" />
            <p className="font-duo-display text-lg text-[var(--text-main)] mb-2">
              {t('certificates.empty.title')}
            </p>
            <p className="text-sm text-[var(--text-muted)] font-medium mb-4">
              {t('certificates.empty.sub')}
            </p>
            <Link to="/courses" className="btn-duo-primary">
              {t('certificates.empty.cta')}
            </Link>
          </div>
        ) : (
          <motion.div
            className="space-y-6"
            variants={maybe(reduced, staggerContainer(0.08, 0.05))}
            initial="hidden"
            animate="visible"
          >
            {certificates.map((cert, i) => (
              <motion.div
                key={cert.certificate_id}
                variants={maybe(reduced, fadeUp)}
                data-testid={`certificate-${i}`}
              >
                <div
                  className={`card-duo p-6 relative overflow-hidden ${cert.status === 'revoked' ? 'opacity-70' : ''}`}
                >
                  {/* Gradient strip */}
                  <div className="absolute top-0 left-0 right-0 h-1.5 bg-gradient-to-r from-[var(--brand-primary)] via-[var(--brand-accent)] to-[var(--brand-success)]" aria-hidden="true" />

                  <div className="flex items-start gap-6">
                    {/* Icon box */}
                    <div className="w-16 h-16 rounded-2xl bg-[var(--surface-warm)] flex items-center justify-center flex-shrink-0">
                      <Award size={32} strokeWidth={2.5} className="text-[var(--brand-accent)]" aria-hidden="true" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-duo-display text-xl mb-1">
                        {t('certificates.card.completionTitle')}
                      </h3>
                      <p className="text-[var(--brand-primary)] font-bold mb-2">
                        {lang === 'tr' ? cert.course_title_tr || cert.course_title : cert.course_title}
                      </p>
                      <p className="text-sm text-[var(--text-muted)] font-medium mb-1">
                        {t('certificates.card.issuedLabel')}: {new Date(cert.issued_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')}
                      </p>
                      <p className="text-xs text-[var(--text-muted)] font-mono">
                        {t('certificates.card.verificationLabel')}: {cert.verification_code}
                      </p>
                    </div>
                    <div className="flex flex-col gap-2">
                      {/* View button */}
                      <button
                        onClick={() => viewPdf(cert.certificate_id)}
                        className="p-2.5 rounded-lg bg-[var(--duo-sky)] text-[var(--duo-blue)] hover:bg-[var(--duo-stroke)] transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                        aria-label={t('certificates.view.ariaLabel')}
                        data-testid={`view-cert-${i}`}
                      >
                        <FileText size={18} aria-hidden="true" />
                      </button>
                      {/* Download button */}
                      <button
                        onClick={() => downloadPdf(cert.certificate_id, cert)}
                        className="p-2.5 rounded-lg bg-[var(--duo-sky)] text-[var(--duo-blue)] hover:bg-[var(--duo-stroke)] transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                        aria-label={t('certificates.download.ariaLabel')}
                        data-testid={`download-cert-${i}`}
                      >
                        <Download size={18} aria-hidden="true" />
                      </button>
                      {/* Share button */}
                      <button
                        onClick={() => shareCertificate(cert)}
                        className="p-2.5 rounded-lg bg-[var(--duo-sky)] text-[var(--duo-blue)] hover:bg-[var(--duo-stroke)] transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                        aria-label={t('certificates.share.ariaLabel')}
                        data-testid={`share-cert-${i}`}
                      >
                        <Share2 size={18} aria-hidden="true" />
                      </button>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-[var(--duo-stroke)] flex items-center gap-2">
                    {cert.status === 'revoked' ? (
                      <>
                        <XCircle size={16} className="text-red-500" aria-hidden="true" />
                        <span className="text-sm font-semibold text-red-600">
                          {t('certificates.status.revoked')}
                        </span>
                        {cert.revoked_at && (
                          <span className="text-xs text-red-400">
                            ({new Date(cert.revoked_at).toLocaleDateString(lang === 'tr' ? 'tr-TR' : 'en-US')})
                          </span>
                        )}
                      </>
                    ) : (
                      <>
                        <CheckCircle size={16} className="text-[var(--brand-success)]" aria-hidden="true" />
                        <span className="text-sm font-semibold text-[var(--brand-success)]">
                          {t('certificates.status.verified')}
                        </span>
                      </>
                    )}
                    <span className="text-xs text-[var(--text-muted)] ml-auto">{user?.name}</span>
                    <ChevronRight size={14} className="text-[var(--text-muted)]" aria-hidden="true" />
                  </div>

                  {/* Dijital Rozet (T.C. Sanayi ve Teknoloji Bakanlığı) — dr_status
                      yoksa (bu kurs rozet programında değil / henüz kuyruğa
                      girmemiş ayrımı frontend'den yapılamıyor) HİÇBİR ŞEY
                      render edilmez, mevcut kart aynen kalır. */}
                  {cert.dr_status && DR_STATUS_CONFIG[cert.dr_status] && (() => {
                    const drConfig = DR_STATUS_CONFIG[cert.dr_status];
                    const DrIcon = drConfig.icon;
                    const showBadgeImage = cert.dr_badge_image && !badgeImgErrored.has(cert.certificate_id);
                    return (
                      <div
                        className="mt-4 pt-4 border-t border-[var(--duo-stroke)]"
                        aria-live="polite"
                        data-testid={`dr-status-${cert.dr_status}-${i}`}
                      >
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-xl overflow-hidden flex items-center justify-center shrink-0 bg-[var(--surface-warm)]">
                            {showBadgeImage ? (
                              <img
                                src={cert.dr_badge_image}
                                alt={t('certificates.dr.badgeImageAlt')}
                                className="w-full h-full object-contain"
                                onError={() => markBadgeImageError(cert.certificate_id)}
                              />
                            ) : (
                              <DrIcon size={18} className={drConfig.iconColor} aria-hidden="true" />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <Badge variant={drConfig.badgeVariant} className="mb-1">
                              {t(drConfig.titleKey)}
                            </Badge>
                            <p className="text-xs text-[var(--text-muted)]">{t(drConfig.bodyKey)}</p>

                            {cert.dr_status === 'sent' && (
                              <div className="flex flex-wrap items-center gap-2 mt-2">
                                {cert.dr_verification_link && (
                                  <a
                                    href={cert.dr_verification_link}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-duo-blue px-4 py-2 text-xs inline-flex items-center"
                                    data-testid={`dr-view-badge-${i}`}
                                  >
                                    {t('certificates.dr.sent.viewBadge')}
                                  </a>
                                )}
                                <button
                                  type="button"
                                  onClick={() => shareDijitalRozetLinkedin(cert)}
                                  disabled={cert.status === 'revoked' || linkedinSharingId === cert.certificate_id}
                                  className="btn-duo-secondary px-4 py-2 text-xs inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
                                  data-testid={`dr-share-linkedin-${i}`}
                                >
                                  <Linkedin size={14} aria-hidden="true" />
                                  {linkedinSharingId === cert.certificate_id ? '...' : t('certificates.dr.sent.shareLinkedin')}
                                  {cert.status === 'revoked' && (
                                    <span className="sr-only">{t('certificates.dr.linkedin.revokedSrOnly')}</span>
                                  )}
                                </button>
                              </div>
                            )}

                            {cert.dr_status === 'awaiting_consent' && (
                              <Link
                                to="/profile?tab=privacy"
                                className="inline-block mt-2 text-xs font-bold text-[var(--brand-primary)] underline"
                                data-testid={`dr-give-consent-${i}`}
                              >
                                {t('certificates.dr.awaitingConsent.cta')}
                              </Link>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              </motion.div>
            ))}
          </motion.div>
        )}
      </div>
    </div>
  );
}
